import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { addDoc, serverTimestamp, collection } from 'firebase/firestore';
import { db } from '../FirebaseConfig';
import { Star } from 'lucide-react';
import MissionImg from "../assets/mission.png"
import * as THREE from 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.module.js';

// Placeholder images for a professional look
const placeholderImages = {
    hero: "https://placehold.co/1200x600/1e293b/d1d5db?text=Professional+Team+Photo",
    mission: MissionImg,
};

const About = () => {
    // State to manage UI visibility and order details
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);

    // Ref for the Three.js canvas
    const canvasRef = useRef(null);
    const mouse = useRef({ x: 0, y: 0 });

    // useEffect hook to set up the 3D background animation
    useEffect(() => {
        if (!canvasRef.current) return;

        // --- Three.js Setup ---
        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
        const renderer = new THREE.WebGLRenderer({ canvas: canvasRef.current, antialias: true, alpha: true });
        renderer.setSize(window.innerWidth, window.innerHeight);

        // --- Create 3D Object (Low-poly Icosahedron) ---
        // IcosahedronGeometry creates a 20-sided object, which looks great with low detail.
        const geometry = new THREE.IcosahedronGeometry(200, 1);
        const material = new THREE.MeshPhongMaterial({
            color: 0x22c55e, // A nice green color
            shininess: 50,
            flatShading: true,
        });
        const object = new THREE.Mesh(geometry, material);
        scene.add(object);

        // --- Add Lighting to the Scene ---
        // Ambient light for general scene illumination
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
        scene.add(ambientLight);

        // Directional light to create shadows and highlights
        const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
        directionalLight.position.set(0, 0, 500);
        scene.add(directionalLight);

        camera.position.z = 400;

        // --- Animation Loop ---
        const animate = () => {
            requestAnimationFrame(animate);

            // Rotate the object slightly on its own
            object.rotation.x += 0.0005;
            object.rotation.y += 0.001;

            // Make the object follow the mouse position for an interactive effect
            const targetRotationX = mouse.current.y * 0.005;
            const targetRotationY = mouse.current.x * 0.005;
            object.rotation.x += (targetRotationX - object.rotation.x) * 0.02;
            object.rotation.y += (targetRotationY - object.rotation.y) * 0.02;

            renderer.render(scene, camera);
        };

        // --- Event Listeners for Interactivity and Responsiveness ---
        const onMouseMove = (event) => {
            mouse.current.x = (event.clientX / window.innerWidth) * 2 - 1;
            mouse.current.y = -(event.clientY / window.innerHeight) * 2 + 1;
        };

        const onWindowResize = () => {
            camera.aspect = window.innerWidth / window.innerHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(window.innerWidth, window.innerHeight);
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('resize', onWindowResize);

        animate();

        // --- Cleanup function on component unmount ---
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('resize', onWindowResize);
            renderer.dispose();
            while(scene.children.length > 0){
                scene.remove(scene.children[0]);
            }
        };
    }, []); // Empty dependency array ensures this runs once on mount

    const handleOrderClick = (product) => {
        setSelectedProduct(product);
        setShowConfirm(true);
    };

    const confirmOrder = async () => {
        if (!selectedProduct) return;
        setLoading(true);
        try {
            await addDoc(collection(db, 'Orders'), {
                productName: selectedProduct.name,
                price: selectedProduct.price,
                productType: 'bestseller',
                timestamp: serverTimestamp(),
            });
            setShowConfirm(false);
            setSelectedProduct(null);
        } catch (err) {
            console.error("Order Error:", err);
        }
        setLoading(false);
    };

    return (
        <div className="relative font-['Space_Grotesk'] min-h-screen text-white bg-transparent">
            {/* The canvas for the 3D animation. It's positioned to cover the whole background. */}
            <canvas ref={canvasRef} className="fixed top-0 left-0 w-full h-full z-0"></canvas>

            {/* ====== Hero Section: About Us ====== */}
            <section className="relative flex items-center justify-center text-center px-4 py-24 z-10">
                <div className="bg-white/10 backdrop-blur-lg p-10 rounded-3xl shadow-2xl">
                    <h1 className="text-5xl font-extrabold mb-4">About Our Store</h1>
                    <p className="text-xl font-light max-w-2xl mx-auto">
                        We are dedicated to providing the best products and a seamless shopping experience for you.
                    </p>
                </div>
            </section>

            {/* ====== Our Story Section ====== */}
            <motion.section
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true, amount: 0.5 }}
                className="max-w-6xl mx-auto px-4 py-16 bg-transparent z-10"
            >
                <div className="flex flex-col md:flex-row items-center gap-12">
                    <div className="md:w-1/2 bg-white/10 p-8 rounded-3xl backdrop-blur-lg shadow-2xl">
                        <h2 className="text-4xl font-bold text-green-400 mb-6">Our Story</h2>
                        <p className="text-gray-200 leading-relaxed text-lg mb-4">
                            Founded with a simple mission: to connect people with high-quality products that enrich their lives. What started as a small idea has grown into a thriving community built on trust and passion. We believe in transparency and authenticity in everything we do.
                        </p>
                        <p className="text-gray-200 leading-relaxed text-lg">
                            Every item in our collection is hand-selected and curated with care, ensuring you receive only the best. We are proud of our journey and grateful for every customer who has joined us along the way.
                        </p>
                    </div>
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        whileInView={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.7 }}
                        viewport={{ once: true }}
                        className="md:w-1/2 z-10"
                    >
                        <img
                            src={placeholderImages.mission}
                            alt="Our Story"
                            className="rounded-3xl shadow-2xl w-full object-cover"
                        />
                    </motion.div>
                </div>
            </motion.section>

            {/* ====== Call to Action Section ====== */}
            <motion.section
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="py-16 text-center z-10"
            >
                <div className="bg-white/10 backdrop-blur-lg p-10 rounded-3xl shadow-2xl max-w-4xl mx-auto px-4">
                    <h2 className="text-4xl font-bold mb-4">Ready to find your next favorite item?</h2>
                    <p className="text-lg mb-6">Explore our curated collection and discover quality you can trust.</p>
                    <button className="bg-green-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-green-500 transition-colors shadow-lg">
                        Shop Now
                    </button>
                </div>
            </motion.section>

            {/* ====== APK Download Section ====== */}
            <motion.section
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="py-16 text-center z-10"
            >
                <div className="bg-white/10 backdrop-blur-lg p-10 rounded-3xl shadow-2xl max-w-4xl mx-auto px-4">
                    <h2 className="text-4xl font-bold mb-4">Download Our Android App</h2>
                    <p className="text-lg mb-6">Experience our full range of features and seamless shopping on the go with our mobile app.</p>
                    <a
                        href="https://median.co/share/zrweay" // TODO: Replace this with the actual APK download URL
                        download
                        className="bg-green-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-green-500 transition-colors shadow-lg"
                    >
                        Download APK
                    </a>
                </div>
            </motion.section>

            {/* ====== Confirmation Modal ====== */}
            <AnimatePresence>
                {showConfirm && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-50"
                    >
                        <motion.div
                            initial={{ y: 50, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 50, opacity: 0 }}
                            className="bg-white/10 backdrop-blur-lg text-white rounded-xl shadow-2xl p-8 w-[90%] max-w-md text-center border border-green-500"
                        >
                            <h3 className="text-2xl font-bold text-green-500 mb-4">Confirm Purchase</h3>
                            <p className="text-lg mb-6">
                                Are you sure you want to buy <span className="font-semibold text-white">{selectedProduct?.name}</span>?
                            </p>
                            <div className="flex justify-center gap-4">
                                <motion.button
                                    onClick={confirmOrder}
                                    disabled={loading}
                                    className="bg-green-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-green-500 transition-colors"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    {loading ? "Adding..." : "Yes"}
                                </motion.button>
                                <motion.button
                                    onClick={() => setShowConfirm(false)}
                                    className="border border-green-600 text-green-500 px-6 py-3 rounded-lg font-bold hover:bg-green-700 hover:text-white transition-colors"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    No
                                </motion.button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default About;
